import { Rating } from "./ber-rating";
import { LoanOption } from "./loan";
import { FormType, SeaiPartialBerAddress } from "./selections";
import type { BrpRoadmapConfig } from "@/helpers/brp-roadmap";

export interface BerWowSettings {
  orgKey: string;
  actionButtonText: string;
  mountElementId: string;
  autoaddressKey?: string;
  autoaddressProfile?: string;
  loanOptions: LoanOption[] | string[];
  targetDefaultEnergyRating: string;
  BerWowId?: string;
  apiEndpoint: string;
  defaultLoanOption: number;
  defaultTerm?: {
    min: number;
    max: number;
  };
  locale?: string;
  tourOptions?: {
    disableTour?: boolean;
  };
  recommendationListSettings?: {
    showSubtableIcons?: boolean;
  };
  formOptions?: FormOptions;
  recommendationsPageOptions?: {
    startAgainButton?: boolean;
    menuDisplay?: string | MenuDisplayOption | string[] | MenuDisplayOption[];
    actionButtonPosition?: string | ButtonPosition;
    resetButtonPosition?: string | ButtonPosition;
    tourButtonPosition?: string | ButtonPosition;
    showDiscount?: boolean;
    co2Chart?: string | Co2Chart;
  };
  brpRoadmapOptions?: BrpRoadmapConfig;
  numberFormatOptions?: {
    currency?: Intl.NumberFormatOptions;
    percentage?: Intl.NumberFormatOptions;
  };
  country?: AvailableCountryISO3;
  useWebHistory?: boolean;
  devOpts?: {
    showRatingValue?: boolean;
  };
}

export interface FormOptions {
  formToggleButton?: boolean;
  formInitialOption?: number;
  displayForms?: FormType[] | string[];
}

export enum MenuDisplayOption {
  Recommendations = "recommendations",
  Hidden = "hidden",
  FinancialDetails = "financialDetails",
  CarbonFootprint = "carbonFootprint",
  BerMap = "berMap",
  ActionButton = "actionButton",
  CostsAndSavings = "costsAndSavings",
  Analysis = "analysis",
  BRPassport = "brp",
}

export enum ButtonPosition {
  Default = "default",
  AsideEnd = "aside-end",
  MainPanel = "main-panel",
}

export enum Co2Chart {
  Trees = "trees",
  BarChart = "barChart",
}

export enum AvailableCountryISO3 {
  GBR = "GBR",
  IRL = "IRL",
}

export enum DefaultCurrencyISO3 {
  GBR = "GBP",
  IRL = "EUR",
}

export interface ApplyRecommendationsReport {
  beforeRating: Rating;
  afterRating: Rating;
  totalAverageComfort: number;
  totalAverageSaving: number;
  totalMeasureCost: number;
  totalGrantCost: number;
  totalDiscountCost: number;
  annualCostBefore: number;
  annualCostAfter: number;
  annualNet: number;
  estimatedYearlyCostInPeriod: number;
  estimatedMonthlyCostInPeriod: number;
  estimatedNetYearlyCostInPeriod: number;
  estimatedNetMonthlyCostInPeriod: number;
  estimatedTotalCost: number;
  estimatedTotalCostPlusLoanInterest: number;
  propertyPercentageUplift: number;
  propertyValue: number;
  savingsInPeriod: SavingsInPeriod;
  totalGrantAidDiscountCost: number;
  carbon: CarbonDetails;
  financeDetails: FinanceDetails;
  loanDetails?: LoanDetails;
  otherFinancialGains: OtherFinancialGains;
  grantMessages: GrantMessage[];
  grantExtras: GrantExtra[]
  notifications: ApiNotification[];
  resultsAnalysis: ResultsAnalysis;
  currentBerRatingReport?: CurrentBerRatingReport;
  typologyMatch?: TypologyMatch;
  boiMortgage?: BoiMortgage;
}

interface CurrentBerRatingReport {
  monthlyEnergySavingsInTerm: number;
  monthlyMortgageSavingsFor250k: number;
  monthlyNetCostFor250k: number;
  totalMortgageSavings: number;
  monthlyMortgageSavings: number;
  monthlyNetCost: number;
  annualCostBefore: number;
  annualCostAfter: number;
  totalMeasureCost: number;
  totalGrantCost: number;
  totalDiscountCost: number;
  totalGrantAidDiscountCost: number;
  estimatedTotalCost: number;
  estimatedTotalCostPlusLoanInterest: number;
  estimatedNetYearlyCostInPeriod: number;
  estimatedNetMonthlyCostInPeriod: number;
  estimatedYearlyCostInPeriod: number;
  estimatedMonthlyCostInPeriod: number;
  totalAverageComfort: number;
  savingsInPeriod: SavingsInPeriod;
  loanDetails: LoanDetails;
  loanValidation: {
    details: string;
    unavailableLoansForAmount: LoanOption[];
    alertType: string;
    loanOptionSelected: LoanOption;
  };
  financeDetails: FinanceDetails;
}

interface TypologyMatch {
  typologyDetails: {
    rating: {
      code: string;
      value: number;
    };
    id: string;
    description: string;
  };
  berDetails: {
    address: {
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      county: string;
      postCode: string;
    };
    dateOfIssue: string;
    deapVersion: string;
    berNumber: string;
    berRating: string;
    energyValue: number;
    co2Emissions: number;
    dwellingTypeId: number;
    dwellingType: string;
    yearOfConstruction: string;
    floorArea: number;
  };
}

export interface SavingsInPeriod {
  estimatedSavings: number;
  estimatedYearlySavings: number;
  estimatedMonthlySavings: number;
  periodYrs: number;
}

export interface CarbonDetails {
  trees: number;
  co2EmissionsBefore: number;
  co2EmissionsAfter: number;
}

export interface FinanceDetails {
  investmentCosts: number[];
  estMonthlyReductionInFuel: number[];
  estMonthlyNetCostGain: number[];
  breakEvenPointYear: number;
  costBreakEvenPointYear: number;
}

export interface LoanDetails {
  amount: number;
  amountIncInt: number;
  apr: number;
  estimatedMonthlyCost: number;
  estimatedYearlyCost: number;
  interest: number;
  name: string;
  repaymentPeriodYrs: number;
}

export interface OtherFinancialGains {
  propertyValue: number;
  propertyPercentageUplift: number;
  cO2EmissionsPerMetreSquaredBefore: number;
  cO2EmissionsPerMetreSquaredAfter: number;
}

export interface GrantMessage {
  details: string;
  recommendationId: string;
}

export interface ApiNotification {
  details: string;
  type: string;
}

interface ResultsAnalysis {
  before: BeforeResults;
  after: AfterResults;
  dwellingBefore?: string;
  dwellingAfter?: string;
  appliedRecommendations: AppliedRecommendation[];
  alreadyAppliedRecommendations: any[];
}

interface BeforeResults {
  berRatingValue: number;
  heatLossIndicator: number;
  shMainEnergy: number;
  shSecEnergy: number;
  whMainEnergy: number;
  whSecEnergy: number;
  pumpsFansEnergy: number;
  lightingEnergy: number;
  renew1Energy: number;
  renew2Energy: number;
  renew3Energy: number;
  energyDelivered: number;
  energyProduced: number;
  energy: number;
  energyDeliveredCosts: number;
  energyProducedSaving: number;
  nonCalibratedCosts: number;
  calibrationFactor: number;
  calibratedEnergyCosts: number;
  houseValue: number;
  upliftPercentage: number;
}

interface AfterResults {
  berRatingValue: number;
  heatLossIndicator: number;
  shMainEnergy: number;
  shSecEnergy: number;
  whMainEnergy: number;
  whSecEnergy: number;
  pumpsFansEnergy: number;
  lightingEnergy: number;
  renew1Energy: number;
  renew2Energy: number;
  renew3Energy: number;
  energyDelivered: number;
  energyProduced: number;
  energy: number;
  energyDeliveredCosts: number;
  energyProducedSaving: number;
  nonCalibratedCosts: number;
  calibrationFactor: number;
  calibratedEnergyCosts: number;
  houseValue: number;
  upliftPercentage: number;
}

interface AppliedRecommendation {
  id: string;
  name: string;
  comfortRating: number;
  savingRating: number;
  estimatedCost: number;
  grantCost: number;
  discountCost: number;
}

export enum UKCountryCode {
  EAW = "EAW", // England and Wales
  SCT = "SCT", // Scotland
  NIR = "NIR", // Northern Ireland
}

export interface epcDetails {
  uprn: number;
  address: SeaiPartialBerAddress;
  epcRating: string;
  energyValue: number;
  inspectionDate: string;
}

export interface BoiMortgage {
  estimatedProjectMonthlyCost: number;
  estimatedProjectTotalCost: number;
  monthlyEnergySavingsInTerm: number;
  monthlyMortgageSavings: number;
  totalMortgageSavings: number;
  monthlySavingsInTerm: number;
}

export interface GrantExtra {
  id: string
  creationDateTime: string // ISO datetime
  updatedDateTime: string  // ISO datetime
  name: string
  grantCost: number
  grantFormula: string
  grantCondition: string | null
  grantConditionMessage: string | null
  discountCost: number
}
